import PageHero from "@/components/layout/PageHero";
import SEO from "@/components/layout/SEO";
import FinalCTA from "@/components/home/FinalCTA";
import { BRAND, IMG, PROOF_STATS, BOOKING_URLS } from "@/lib/content";
import { Award, BookOpen, Users, Compass } from "lucide-react";

const METHOD = [
  { icon: Compass, title: "Poland-only", body: "We don't chase every country. Depth over breadth." },
  { icon: BookOpen, title: "University-first", body: "We understand institutions — rankings, programs, actual outcomes." },
  { icon: Users, title: "Student-first", body: "Advice calibrated to your profile, not a template." },
  { icon: Award, title: "Honest-first", body: "We'll tell you when you don't need us. That's the point." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <SEO
        title="About Krzysztof Szymański — Poland higher education expert"
        description="Krzysztof Szymański — International Students Advisor with 15+ years working with Polish higher education. Trusted by international students and families across 100+ universities."
        path="/about"
      />
      <PageHero
        overline="About"
        title={<>The trusted Poland expert for <span className="italic text-[#D7141A]">international students</span>.</>}
        subtitle={`${BRAND.expertName} · ${BRAND.expertTitle}. Fifteen years of direct work with Polish higher education institutions, international students, and families making high-stakes decisions.`}
        image={IMG.krzPortraitAirport}
        ctaPrimary={{ to: BOOKING_URLS.general, label: "Work With Us" }}
      />

      <section className="section-pad" data-testid="about-story">
        <div className="container-sp grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">The story</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1B2A] mt-3 leading-tight">
              Fifteen years inside Polish higher education.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#4A5568] leading-relaxed">
            <p>
              Our work started inside the Polish higher education system — reviewing
              programs, evaluating institutions, and understanding how rankings and
              quality indicators actually function.
            </p>
            <p>
              Over time, international students and families began reaching out with
              the same questions: <em>which university, what will it cost, how do I get
              the visa, and what do I actually need to do first.</em>
            </p>
            <p>
              Today, we bridge two worlds. We understand Polish universities from the
              inside, and we understand international applicants from the outside. That's
              the gap most agents can't fill.
            </p>
            <p>
              Our mission is to become the most trusted and cited expert on international
              student matters in Poland — by being genuinely useful, specific and honest
              with every family we work with.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#EBE9E1]/60" data-testid="about-method">
        <div className="container-sp">
          <div className="max-w-3xl">
            <div className="overline">Method</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1B2A] mt-3 leading-tight">
              How we work.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {METHOD.map((m) => (
              <div key={m.title} className="card-sp">
                <div className="w-11 h-11 rounded-xl bg-[#0D1B2A] text-[#D7141A] flex items-center justify-center">
                  <m.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl text-[#0D1B2A] mt-5">{m.title}</h3>
                <p className="text-sm text-[#4A5568] mt-2.5 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4">
            {PROOF_STATS.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white border border-[#EFEEE8] p-6">
                <div className="font-serif text-3xl text-[#0D1B2A]">{s.value}</div>
                <div className="text-xs text-[#4A5568] mt-1.5 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
